<template>
  <div class="distribution-area-chart">
    <div class="chart-header">
      <h6 class="chart-title">{{ title }}</h6>
      <div class="chart-filters">
        <select v-model="selectedPeriod" class="form-select form-select-sm" @change="onPeriodChange">
          <option value="7">Últimos 7 días</option>
          <option value="15">Últimos 15 días</option>
          <option value="30">Último mes</option>
          <option value="90">Últimos 3 meses</option>
        </select>
        <select v-model="selectedStatus" class="form-select form-select-sm" @change="onStatusChange">
          <option value="">Todos los estados</option>
          <option v-for="status in availableStatuses" :key="status" :value="status">
            {{ formatStatus(status) }}
          </option>
        </select>
      </div>
    </div>

    <div class="chart-container">
      <div v-if="loadingInternal" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      <AreaChartGradient
        v-else-if="chartLabels.length > 0"
        :key="'distribution-' + chartLabels.join('-')"
        :title="''"
        :labels="chartLabels"
        :data="chartData"
        :legend-label="'Cantidad de contratos'"
        :summary="chartSummary"
        :format-tooltip="formatTooltipValue"
        gradient-from="rgba(77, 27, 56, 0.4)"
        gradient-to="rgba(77, 27, 56, 0.02)"
        line-color="#4D1B38"
        :show-legend="false"
      />
      <div v-else class="no-data">
        <p>No hay datos disponibles para el período seleccionado</p>
      </div>
    </div>

    <!-- Leyenda de estados -->
    <div class="status-legend" v-if="statusBreakdown.length > 0">
      <div class="legend-item" v-for="item in statusBreakdown.slice(0, 5)" :key="item.status">
        <span class="legend-color" :style="{ backgroundColor: getStatusColor(item.status) }"></span>
        <span class="legend-label">{{ formatStatus(item.status) }}</span>
        <span class="legend-value">{{ item.count }} ({{ item.percentage }}%)</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import axios from "axios";
import AreaChartGradient from "./AreaChartGradient.vue";

export default {
  name: "StateDistributionAreaChart",
  components: {
    AreaChartGradient,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    rawData: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "Distribución de Estados",
    },
    statusTranslations: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedPeriod: "7",
      selectedStatus: "",
      chartLabels: [],
      chartData: [],
      chartSummary: null,
      statusBreakdown: [],
      availableStatuses: [],
      internalData: [],
      loadingInternal: false,
    };
  },
  watch: {
    rawData: {
      handler(newData) {
        // Cuando recibimos datos del dashboard, procesarlos
        if (newData && newData.length > 0) {
          this.internalData = newData;
          this.processData();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Cargar datos iniciales con el período por defecto
    this.loadDataForPeriod();
  },
  methods: {
    async loadDataForPeriod() {
      this.loadingInternal = true;

      const days = parseInt(this.selectedPeriod);
      const dateFrom = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
      const dateTo = moment().format("YYYY-MM-DD");

      try {
        const isBusinessUser = this.$store?.state?.roles?.Backoffice_member_empresas || false;
        const url = isBusinessUser ? "/business/contract/search" : "/contract/search";

        const params = {
          "pagination.pageSize": 500,
          "pagination.page": 1,
          dateFrom,
          dateTo,
        };

        if (this.selectedStatus) {
          params.statuses = this.selectedStatus;
        }

        const response = await axios.get(url, { params });
        this.internalData = response.data.contracts || [];
        this.processData();
      } catch (error) {
        console.error("Error loading distribution data:", error);
        // Si falla, usar los datos del rawData
        this.internalData = this.rawData || [];
        this.processData();
      } finally {
        this.loadingInternal = false;
      }
    },

    processData() {
      const dataToProcess = this.internalData.length > 0 ? this.internalData : this.rawData;

      if (!dataToProcess || dataToProcess.length === 0) {
        this.chartLabels = [];
        this.chartData = [];
        this.chartSummary = null;
        this.statusBreakdown = [];
        return;
      }

      // Extraer estados únicos
      this.availableStatuses = [...new Set(dataToProcess.map(item => this.getItemStatus(item)))];

      // Generar días del período
      const days = parseInt(this.selectedPeriod);
      const startDate = moment().subtract(days - 1, "days").startOf("day");
      const endDate = moment().endOf("day");

      const dailyData = {};
      const currentDate = startDate.clone();

      while (currentDate.isSameOrBefore(endDate, "day")) {
        dailyData[currentDate.format("YYYY-MM-DD")] = 0;
        currentDate.add(1, "day");
      }

      // Filtrar por estado si hay uno seleccionado
      let filteredData = dataToProcess;
      if (this.selectedStatus) {
        filteredData = dataToProcess.filter(item =>
          this.getItemStatus(item).toUpperCase() === this.selectedStatus.toUpperCase()
        );
      }

      // Calcular breakdown de estados (del total sin filtrar por estado)
      const statusCount = {};
      dataToProcess.forEach(item => {
        const status = this.getItemStatus(item);
        statusCount[status] = (statusCount[status] || 0) + 1;
      });

      this.statusBreakdown = Object.entries(statusCount)
        .map(([status, count]) => ({
          status,
          count,
          percentage: ((count / dataToProcess.length) * 100).toFixed(1),
        }))
        .sort((a, b) => b.count - a.count);

      // Contar por día usando createdAt
      filteredData.forEach(item => {
        const date = moment(item.createdAt || item.date || item.dateOfRequest);
        if (date.isValid()) {
          const dayKey = date.format("YYYY-MM-DD");
          if (dailyData[dayKey] !== undefined) {
            dailyData[dayKey]++;
          }
        }
      });

      // Preparar datos para el chart
      const labels = [];
      const data = [];

      Object.keys(dailyData).sort().forEach(dayKey => {
        const dayMoment = moment(dayKey);
        labels.push(dayMoment.format("ddd DD"));
        data.push(dailyData[dayKey]);
      });

      this.chartLabels = labels;
      this.chartData = data;

      // Calcular resumen
      const total = data.reduce((a, b) => a + b, 0);
      const avg = data.length > 0 ? total / data.length : 0;
      const min = data.length > 0 ? Math.min(...data) : 0;
      const max = data.length > 0 ? Math.max(...data) : 0;

      this.chartSummary = {
        avg: Math.round(avg).toString(),
        min: min.toString(),
        max: max.toString(),
      };
    },

    getItemStatus(item) {
      if (item.status) {
        if (typeof item.status === "object") {
          return item.status.status || "UNKNOWN";
        }
        return item.status;
      }
      return item.state || "UNKNOWN";
    },

    formatStatus(status) {
      if (!status) return "Desconocido";
      const upperStatus = status.toUpperCase();
      if (this.statusTranslations[upperStatus]) {
        return this.statusTranslations[upperStatus];
      }
      if (this.statusTranslations[status]) {
        return this.statusTranslations[status];
      }
      return status.replace(/_/g, " ");
    },

    formatTooltipValue(value) {
      return `${value} contratos`;
    },

    getStatusColor(status) {
      const colors = {
        "CREATED": "#6366f1",
        "PENDING_VALIDATION": "#f59e0b",
        "PENDING_SCORING": "#f97316",
        "PENDING_PAYMENT": "#ef4444",
        "ACCEPTED": "#22c55e",
        "ACTIVE": "#10b981",
        "PENDING_DELIVERY": "#8b5cf6",
        "DELIVERED": "#06b6d4",
        "REJECTED": "#dc2626",
        "CANCELLED": "#6b7280",
        "FINISHED": "#14b8a6",
      };
      return colors[status.toUpperCase()] || "#94a3b8";
    },

    onPeriodChange() {
      this.$emit("period-change", this.selectedPeriod);
      this.loadDataForPeriod();
    },

    onStatusChange() {
      this.$emit("status-change", this.selectedStatus);
      this.loadDataForPeriod();
    },
  },
};
</script>

<style scoped lang="scss">
.distribution-area-chart {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.chart-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.chart-filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.form-select-sm {
  padding: 0.375rem 2rem 0.375rem 0.75rem;
  font-size: 0.8125rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  min-width: 140px;

  &:focus {
    border-color: #cc0b7a;
    box-shadow: 0 0 0 2px rgba(204, 11, 122, 0.1);
  }
}

.chart-container {
  min-height: 300px;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #6b7280;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #6b7280;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.legend-label {
  color: #374151;
}

.legend-value {
  color: #6b7280;
  font-weight: 500;
}

@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .chart-filters {
    width: 100%;
  }

  .form-select-sm {
    flex: 1;
    min-width: 0;
  }
}
</style>
